import { fn, IInfOperation } from '@common/utils';
import { RwfBootstrap, IRwfBootstrapParameters, TClientCredentialType } from '@common/rwf';
import { CompatibilityService } from '../../services/compatibilityservice';
import { IInfNotification } from '@common/navkendo';

export class CtrlChangeNavUserPassword {
    /** Init Notification of "Password is about to get invalid" functionality */
    static Init = ["CompatibilityService", "RwfBootstrap", "KWFNotificationService",
        function Init(
            $c: CompatibilityService,
            $rwfBootstrap: RwfBootstrap,
            notification: IInfNotification) {
            $rwfBootstrap.OnResolved(rwf => {
                const rwfUser = rwf.RwfUser;

                if (CtrlChangeNavUserPassword.ValidateCanChangePassword(rwf, false)
                    && rwfUser.ShowPasswordExpiryMessage) {

                    const DaysUntilPasswordExpires = rwfUser.DaysUntilPasswordExpires;

                    notification.AddNotification({
                        message: ExpiresMessage(DaysUntilPasswordExpires, true) +
                            `<button style="margin-left:2em" inf-operation='::dataItem.PwdAction' class='k-button'>Passwort ändern</button>`,
                        messageType: "info",
                        customData: { PwdAction: CtrlChangeNavUserPassword.ChangePasswordAction($c) }
                    });
                }
            });
        }];

    /** Construct ChangePassword Action */
    static ChangePasswordAction($c: CompatibilityService) {
        const ctrl = CtrlChangeNavUserPassword;
        var result: IInfOperation & { Hint?: string } = {
            Hidden: "Enabled",
            Execute: () => ctrl.ModalChangePassword($c),
            Enabled: false,
        };

        $c.resolve("Rwf")("RwfBootstrap").Resolve.then(rwf => {
            result.Enabled = ctrl.ValidateCanChangePassword(rwf, false);
            result.Hint = ExpiresMessage(rwf.RwfUser.DaysUntilPasswordExpires);
        });
        return result;
    }

    static ChangeUserAction($c: CompatibilityService) {
        var result: IInfOperation = {
            Hidden: "Enabled",
            Execute: () => $c.resolve("DialogService").ShowConfirm("Wollen Sie sich wirklich abmelden und mit anderem Benutzer anmelden?")
                .then(answer => {
                    if (answer) {
                        InvalidatePwdAndreload($c);
                    }
                }),
            Enabled: false,
        };

        $c.resolve("Rwf")("RwfBootstrap").Resolve.then(rwf => {
            result.Enabled = CtrlChangeNavUserPassword.ValidateCanChangePassword(rwf, false);
            if(result.Enabled) {
              result.Title = "Benutzer wechseln"
            }
            else if(rwf.RwfSettings.Authentication == "InfomaOnline") {
              result.Enabled = true;
              result.Title = "Abmelden",
              result.Execute = () => {
                window.location = "./SignOut" as any;
              }
            }
          });
        return result;
    }

    /** Modal let User Change Password, will reload page on success */
    static ModalChangePassword($c: CompatibilityService) {
        return $c.resolve("DialogService").ShowWindow({
            controller: CtrlChangeNavUserPassword,
            controllerAs: "vm",
            template: require('./changepassword.html'),
            windowOptions: {
                actions: ["close"],
                modal: true,
                title: "Passworteinstellungen",
                resizable: false
            },
            resolve: {
                "RwfBootstrapParameters": RwfBootstrap.Resolve
            }
        }).then(w => {
            return w.close;
        });
    }

    static ValidateCanChangePassword($rwf: IRwfBootstrapParameters, throwError: boolean) {
        const doReturn = (msg: string) => {
            if (throwError) {
                throw new Error(msg);
            }
            return false;
        };
        const validTypes: TClientCredentialType[] = ["NavUserPassword", "NavUser", "FullExternal"];
        const credType: TClientCredentialType = <any>$rwf.RwfSetup.ClientCredentialType;
        if (validTypes.indexOf(credType) < 0 || $rwf.RwfSettings.Authentication == "InfomaOnline")
            return doReturn(`Ändern des Passworts ist bei Anmeldetyp '${credType}' nicht möglich!`);

        return true;
    }

    static $inject = ["CompatibilityService", "RwfBootstrapParameters"];

    constructor(readonly $c: CompatibilityService, private $rwfParams: IRwfBootstrapParameters) {
        this.ExpirationInfo = ExpiresMessage($rwfParams.RwfUser.DaysUntilPasswordExpires);
        const ActChangePassword = this.ActChangePassword = {
            Enabled: <boolean>true,
            Execute: () => {
                if (ActChangePassword.Enabled) {
                    ActChangePassword.Enabled = false;
                    return $c.finally(this.Execute(),
                        () => ActChangePassword.Enabled = true).then(fn.noop,
                            err => $c.resolve("DialogService").ShowError(err));
                }
            }
        };
    }

    public Execute() {
        const $c = this.$c;
        const NavUrl = $c.resolve("NavUrl");
        const NavClient = $c.resolve("NavClient");
        delete this.CurrentError;

        CtrlChangeNavUserPassword.ValidateCanChangePassword(this.$rwfParams, true);

        return NavUrl.NavUrl.then(navUrl => {
            const rawString = JSON.stringify(this.PwdInfo);
            return NavClient.Encrpyt(rawString, true).then(encryptedValue => {
                const fullUrl = WEBAPI_ChangeNavUserPassword + "?" +
                    fn.EncodeQueryData({ navUrl: navUrl.FullUrl });
                return $c.post(fullUrl, { Encrpyted: encryptedValue }).then(r => {
                    fn.resolveToError(r.data, true);
                    return InvalidatePwdAndreload($c);
                }, err => {
                    this.CurrentError = fn.resolveToError(err);
                    throw err;
                });
            });
        });
    }

    public ExpirationInfo: string;

    public CurrentError: string;

    public readonly PwdInfo = {
        CurrentPassword: "",
        NewPassword: "",
        NewPasswordRepeat: ""
    };

    /** Action to Change Password */
    public readonly ActChangePassword: IInfOperation;
}

function InvalidatePwdAndreload($c: CompatibilityService) {
    return $c.resolve("NavUrl").NavUrl.then(navUrl => {
        const fullUrl = WEBAPI_ChangeNavUserPassword + "?" +
            fn.EncodeQueryData({ navUrl: navUrl.FullUrl });

        var result = $c.defer<boolean>();
        //Submits an invalid authentication header, causing the user to be 'logged out'
        $.ajax({
            type: "GET",
            url: fullUrl,
            dataType: 'json',
            async: true,
            username: "<username>",
            password: "<password>",
            data: '{ "comment" }'
        }).done(function () {
            // Invalid Auth must fail
            result.reject("Failed!");
        }).fail(function () {
            result.resolve(true);
            window.location.reload(true);
        });
        return result.promise;
    });
}

function ExpiresMessage(DaysUntilPasswordExpires: number, forceMessage?: boolean) {
    if (DaysUntilPasswordExpires < 0 || typeof (DaysUntilPasswordExpires) !== "number" || isNaN(DaysUntilPasswordExpires)) {
        return forceMessage ? 'Ihr Passwort ist abgelaufen!' : '';
    }
    return DaysUntilPasswordExpires > 0 ? `Ihr Passwort läuft in ${DaysUntilPasswordExpires} Tag${DaysUntilPasswordExpires !== 1 ? "en" : ""} ab!`
        : 'Ihr Passwort ist abgelaufen!';

}
const WEBAPI_ChangeNavUserPassword = "infoma.Web.KWF3/ChangeNavUserPassword";
